import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, SignOut, Key, Trash, MagnifyingGlass, ArrowLeft, User as UserIcon, Warning,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [resetting, setResetting] = useState(null); // user object being reset
  const [newPassword, setNewPassword] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    try {
      const res = await api.post(`/admin/users/${resetting.user_id}/reset-password`, { new_password: newPassword });
      toast.success(`Password updated for ${res.data.email}`);
      setResetting(null);
      setNewPassword("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const doDelete = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = users.filter((u) =>
    (u.email || "").toLowerCase().includes(query.toLowerCase()) ||
    (u.name || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      {/* Topbar */}
      <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-zinc-900">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-500 hover:text-white transition-colors" data-testid="admin-back-btn"><ArrowLeft size={20} /></button>
          <ShieldCheck size={24} weight="duotone" className="text-[#0055FF]" />
          <div>
            <h1 className="font-heading font-black text-2xl tracking-tighter">Admin Console</h1>
            <p className="font-mono text-xs text-zinc-500">Manage users · {users.length} total</p>
          </div>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn" className="text-zinc-500 hover:text-[#FF3B30] transition-colors"><SignOut size={22} /></button>
      </div>

      <div className="p-6 md:p-10 max-w-5xl">
        {/* Search */}
        <div className="relative mb-6">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="admin-search-input"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email or name..."
            className="w-full bg-zinc-900 border border-zinc-800 pl-10 pr-4 py-3 text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none transition-colors"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="loader-square" /></div>
        ) : (
          <div className="border border-zinc-800 bg-[#121214]" data-testid="admin-user-table">
            {/* header row */}
            <div className="hidden md:grid grid-cols-[1fr_1fr_120px_140px_180px] gap-4 px-6 py-3 border-b border-zinc-800 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
              <span>User</span><span>Email</span><span>Role</span><span>Created</span><span className="text-right">Actions</span>
            </div>
            {filtered.length === 0 && <p className="px-6 py-8 text-zinc-600 font-mono text-sm">No users found.</p>}
            {filtered.map((u) => (
              <motion.div
                key={u.user_id}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="grid grid-cols-2 md:grid-cols-[1fr_1fr_120px_140px_180px] gap-4 px-6 py-4 border-b border-zinc-900 items-center"
                data-testid="admin-user-row"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center shrink-0"><UserIcon size={16} className="text-zinc-400" /></div>
                  <span className="truncate text-sm">{u.name || "—"}</span>
                </div>
                <span className="font-mono text-xs text-zinc-300 truncate" data-testid="admin-user-email">{u.email}</span>
                <span className={`font-mono text-[11px] uppercase ${u.role === "admin" ? "text-[#00E5FF]" : "text-zinc-500"}`}>{u.role || "user"}</span>
                <span className="hidden md:block font-mono text-[11px] text-zinc-500">{fmtDate(u.created_at).split(",")[0]}</span>
                <div className="col-span-2 md:col-span-1 flex items-center justify-end gap-2">
                  <button
                    onClick={() => { setResetting(u); setNewPassword(""); }}
                    data-testid="admin-reset-btn"
                    className="flex items-center gap-1.5 border border-zinc-700 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider"
                  >
                    <Key size={14} /> Reset
                  </button>
                  {u.role !== "admin" && (
                    <button
                      onClick={() => setConfirmDelete(u)}
                      data-testid="admin-delete-btn"
                      className="text-zinc-600 hover:text-[#FF3B30] transition-colors p-1.5"
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Reset password modal */}
      {resetting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="reset-modal">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-[#121214] border border-zinc-800 p-8">
            <div className="flex items-center gap-2 mb-2"><Key size={18} className="text-[#0055FF]" /><span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Reset Password</span></div>
            <p className="text-sm text-zinc-400 mb-6">Set a new password for <span className="text-white font-mono">{resetting.email}</span>. All their active sessions will be signed out.</p>
            <form onSubmit={doReset} className="space-y-4">
              <input
                data-testid="reset-password-input"
                type="text" autoFocus value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
                className="w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white font-mono focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none transition-colors"
              />
              <div className="flex gap-3">
                <button type="button" onClick={() => setResetting(null)} className="flex-1 border border-zinc-700 py-3 font-mono text-sm uppercase tracking-widest hover:border-zinc-500 transition-colors" data-testid="reset-cancel-btn">Cancel</button>
                <button type="submit" className="flex-1 bg-[#0055FF] text-white py-3 font-mono text-sm uppercase tracking-widest hover:bg-blue-700 transition-colors" data-testid="reset-confirm-btn">Update</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete confirm modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="delete-modal">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-[#121214] border border-zinc-800 p-8">
            <div className="flex items-center gap-2 mb-2"><Warning size={18} className="text-[#FF3B30]" /><span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Delete User</span></div>
            <p className="text-sm text-zinc-400 mb-6">Permanently delete <span className="text-white font-mono">{confirmDelete.email}</span> and all their devices? This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 border border-zinc-700 py-3 font-mono text-sm uppercase tracking-widest hover:border-zinc-500 transition-colors" data-testid="delete-cancel-btn">Cancel</button>
              <button onClick={() => doDelete(confirmDelete)} className="flex-1 bg-[#FF3B30] text-white py-3 font-mono text-sm uppercase tracking-widest hover:bg-red-700 transition-colors" data-testid="delete-confirm-btn">Delete</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
